import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Container,
  InputLabel,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Avatar,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ClickAwayListener,
  Popper,
  Fade,
  Paper as MuiPaper,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocalAtm as LocalAtmIcon,
  AccountBalance as AccountBalanceIcon,
  Diamond as DiamondIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { DataContext } from "../../context/DataContext";
import { format, parseISO, addDays } from "date-fns";
import { useTheme } from "@mui/material/styles";

const JewelleryDaybook = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const {
    customers,
    suppliers,
    loading,
    roznama,
    closeRoznamaDay,
    refresh,
    updateRoznama,
    addRoznama,
    customerTransactions,
    supplierTransactions,
    recalculateRoznamas
  } = useContext(DataContext);

  // ============ STATE ============
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedRoznama, setSelectedRoznama] = useState(null);
  const [loadingRoznama, setLoadingRoznama] = useState(false);
  const [openingBalance, setOpeningBalance] = useState({ cash: 0, bank: 0, fine: 0 });
  const [manualTransactions, setManualTransactions] = useState({ income: [], expense: [] });
  const [isClosingDay, setIsClosingDay] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [metalRate, setMetalRate] = useState();

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [closeDayDialogOpen, setCloseDayDialogOpen] = useState(false);

  // Transaction form state
  const [newTransaction, setNewTransaction] = useState({
    type: "income",
    billNumber: "",
    description: "",
    amount: "",
    paymentMode: "cash",
    cashPortion: "",
    bankPortion: "",
    fineReceived: "",
    finePaid: "",
    partyName: "",
    partyType: "manual",
    customerId: null,
    supplierId: null,
    conversionType: "none", // "cashForFine", "fineForCash"
    conversionAmount: "",
    conversionNote: "",
  });

  // Search states with Popper
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPopperOpen, setCustomerPopperOpen] = useState(false);
  const customerAnchorRef = useRef(null);

  const [supplierQuery, setSupplierQuery] = useState("");
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPopperOpen, setSupplierPopperOpen] = useState(false);
  const supplierAnchorRef = useRef(null);

  // Close day states
  const [actualBalances, setActualBalances] = useState({ cash: 0, bank: 0, fine: 0 });
  const [differences, setDifferences] = useState({ cash: 0, bank: 0, fine: 0, total: 0 });

  // ============ HELPERS ============
  const convertDateToYYYYMMDD = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return null;
    }
  };

  const isPastDay = useCallback(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    return selectedDate < today;
  }, [selectedDate]);

  const isDayClosed = useCallback(() => {
    return false;
  }, []);

  const formatCurrency = (value) => {
    return `₹${(parseFloat(value) || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const formatFine = (value) => {
    return `${(parseFloat(value) || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} gm`;
  };

  // ============ GET CUSTOMER/SUPPLIER BALANCES ============
  const getCustomerBalance = useCallback((customerId) => {
    if (!customerId) return null;
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return null;

    const transactions = customerTransactions?.filter(t =>
      t.customer === customerId && t.transactionDetails?.type !== 'cancelled'
    ) || [];

    // Initialize balances with opening balances - ensure numbers
    let cashBalance = Number(customer?.openingBalanceCash) || 0;
    let bankBalance = Number(customer?.openingBalanceBank) || 0;
    let fineBalance = Number(customer?.openingBalanceFine) || 0;

    let cashCredit = 0;
    let cashDebit = 0;
    let bankCredit = 0;
    let bankDebit = 0;
    let fineCredit = 0;
    let fineDebit = 0;

    transactions.forEach((t) => {
      // Convert all values to numbers explicitly
      const grandTotal = Number(t?.summary?.grandTotal) || 0;
      const isGstApplicable = t?.transactionDetails?.gstApplicable === true;
      const paymentHistory = t?.paymentDetails || {};
      const calculatedTotals = t?.calculatedTotals || {};

      // Calculate payments - ensure all are numbers
      let cashPayment = Number(paymentHistory.cash) || 0;
      let bankPayment = (Number(paymentHistory.online) || 0) + (Number(paymentHistory.cheque) || 0);
      let finePayment = Number(calculatedTotals?.finePaymentReceived) || 0;

      // Credit amounts
      let cashCreditAmount = 0;
      let bankCreditAmount = 0;
      let fineCreditAmount = Number(calculatedTotals?.totalFineWeight) || 0;

      if (isGstApplicable) {
        bankCreditAmount = grandTotal;
      } else {
        cashCreditAmount = grandTotal;
      }

      // Debit amounts
      let cashDebitAmount = cashPayment;
      let bankDebitAmount = bankPayment;
      let fineDebitAmount = finePayment;

      // Update balances - using numeric addition
      cashBalance = cashBalance + cashCreditAmount - cashDebitAmount;
      bankBalance = bankBalance + bankCreditAmount - bankDebitAmount;
      fineBalance = fineBalance + fineCreditAmount - fineDebitAmount;

      cashCredit += cashCreditAmount;
      cashDebit += cashDebitAmount;
      bankCredit += bankCreditAmount;
      bankDebit += bankDebitAmount;
      fineCredit += fineCreditAmount;
      fineDebit += fineDebitAmount;
    });

    return {
      customer,
      cashBalance: cashBalance.toFixed(2),
      bankBalance: bankBalance.toFixed(2),
      fineBalance: fineBalance.toFixed(3),
      cashCredit: cashCredit.toFixed(2),
      cashDebit: cashDebit.toFixed(2),
      bankCredit: bankCredit.toFixed(2),
      bankDebit: bankDebit.toFixed(2),
      fineCredit: fineCredit.toFixed(3),
      fineDebit: fineDebit.toFixed(3),
    };
  }, [customers, customerTransactions]);

  const getSupplierBalance = useCallback((supplierId) => {
    if (!supplierId) return null;
    const supplier = suppliers.find(s => s.id === supplierId);
    if (!supplier) return null;

    const transactions = supplierTransactions?.filter(t =>
      t.supplier === supplierId && t.transactionDetails?.type !== 'cancelled'
    ) || [];

    // Initialize balances with opening balances - ensure numbers
    let cashBalance = Number(supplier?.openingBalanceCash) || 0;
    let bankBalance = Number(supplier?.openingBalanceBank) || 0;
    let fineBalance = Number(supplier?.openingBalanceFine) || 0;

    let cashCredit = 0;
    let cashDebit = 0;
    let bankCredit = 0;
    let bankDebit = 0;
    let fineCredit = 0;
    let fineDebit = 0;

    transactions.forEach((t) => {
      // Convert all values to numbers explicitly
      const grandTotal = Number(t?.summary?.grandTotal) || 0;
      const isGstApplicable = t?.transactionDetails?.gstApplicable === true;
      const paymentHistory = t?.payments || {};
      const summaryTrans = t?.summary || {};

      let cashPayment = Number(paymentHistory.cash) || 0;
      let bankPayment = Number(paymentHistory.bank) || 0;
      let finePayment = Number(summaryTrans?.finePayment) || 0;

      let bankCreditAmount = 0;
      let cashCreditAmount = 0;
      let fineCreditAmount = Number(summaryTrans?.fineBalance) || 0;

      if (isGstApplicable) {
        bankCreditAmount = grandTotal;
      } else {
        cashCreditAmount = grandTotal;
      }

      let cashDebitAmount = cashPayment;
      let bankDebitAmount = bankPayment;
      let fineDebitAmount = finePayment;

      cashBalance = cashBalance + cashCreditAmount - cashDebitAmount;
      bankBalance = bankBalance + bankCreditAmount - bankDebitAmount;
      fineBalance = fineBalance + fineCreditAmount - fineDebitAmount;

      cashCredit += cashCreditAmount;
      cashDebit += cashDebitAmount;
      bankCredit += bankCreditAmount;
      bankDebit += bankDebitAmount;
      fineCredit += fineCreditAmount;
      fineDebit += fineDebitAmount;
    });

    return {
      supplier,
      cashBalance: cashBalance.toFixed(2),
      bankBalance: bankBalance.toFixed(2),
      fineBalance: fineBalance.toFixed(3),
      cashCredit: cashCredit.toFixed(2),
      cashDebit: cashDebit.toFixed(2),
      bankCredit: bankCredit.toFixed(2),
      bankDebit: bankDebit.toFixed(2),
      fineCredit: fineCredit.toFixed(3),
      fineDebit: fineDebit.toFixed(3),
    };
  }, [suppliers, supplierTransactions]);
  // ============ DATA LOADING ============
  const loadRoznamaForDate = useCallback(async (date) => {
    // 🔥 FIX: Don't run if still loading
    if (loadingRoznama || loading) {
      console.log("Skipping loadRoznamaForDate: loadingRoznama=", loadingRoznama, "loading=", loading);
      return;
    }

    setLoadingRoznama(true);
    try {
      const targetDate = convertDateToYYYYMMDD(date);
      console.log(`loadRoznamaForDate: Loading for ${targetDate}`);

      // 🔥 FIX: Check if roznama exists in the context first
      const existingRoznama = roznama?.find(r => convertDateToYYYYMMDD(r.date) === targetDate);

      if (existingRoznama) {
        console.log(`Found existing roznama for ${targetDate} in context`);
        setSelectedRoznama(existingRoznama);
        setOpeningBalance({
          cash: existingRoznama.openingBalanceCash || 0,
          bank: existingRoznama.openingBalanceBank || 0,
          fine: existingRoznama.openingBalanceFine || 0,
        });
        setManualTransactions({
          income: existingRoznama.incomeTransactions || [],
          expense: existingRoznama.expenseTransactions || [],
        });
        setLoadingRoznama(false);
        setIsInitialLoad(false);
        return;
      }

      // 🔥 FIX: Don't create for past dates
      const today = format(new Date(), "yyyy-MM-dd");
      if (targetDate !== today) {
        console.log(`Past date ${targetDate} with no roznama, showing empty state`);
        setSelectedRoznama(null);
        setOpeningBalance({ cash: 0, bank: 0, fine: 0 });
        setManualTransactions({ income: [], expense: [] });
        setLoadingRoznama(false);
        setIsInitialLoad(false);
        return;
      }

      // 🔥 FIX: Only create today's roznama if it doesn't exist
      if (targetDate === today) {
        // Double-check if today's roznama exists (in case it was just loaded)
        const todayRoznama = roznama?.find(r => convertDateToYYYYMMDD(r.date) === today);

        if (todayRoznama) {
          console.log(`Found today's roznama after double-check`);
          setSelectedRoznama(todayRoznama);
          setOpeningBalance({
            cash: todayRoznama.openingBalanceCash || 0,
            bank: todayRoznama.openingBalanceBank || 0,
            fine: todayRoznama.openingBalanceFine || 0,
          });
          setManualTransactions({
            income: todayRoznama.incomeTransactions || [],
            expense: todayRoznama.expenseTransactions || [],
          });
          setLoadingRoznama(false);
          setIsInitialLoad(false);
          return;
        }

        // 🔥 CRITICAL: Don't create if we're still loading data from the API
        if (loading) {
          console.log("Still loading data, skipping creation");
          setLoadingRoznama(false);
          return;
        }

        // Get the last roznama for opening balances
        const sortedRoznama = [...roznama].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB;
        });

        const lastRoznama = sortedRoznama.length > 0 ? sortedRoznama[sortedRoznama.length - 1] : null;

        // Only create if we have data
        if (lastRoznama) {
          console.log(`Creating new roznama for today with opening balance from ${lastRoznama.date}`);
          const createResult = await addRoznama({
            date: today,
            openingBalanceCash: lastRoznama?.closingBalanceCash || 0,
            openingBalanceBank: lastRoznama?.closingBalanceBank || 0,
            openingBalanceFine: lastRoznama?.closingBalanceFine || 0,
            closingBalanceCash: lastRoznama?.closingBalanceCash || 0,
            closingBalanceBank: lastRoznama?.closingBalanceBank || 0,
            closingBalanceFine: lastRoznama?.closingBalanceFine || 0,
            incomeTransactions: [],
            expenseTransactions: [],
            netProfit: 0,
            totalIncome: 0,
            totalExpense: 0
          });

          if (createResult.success) {
            // The context will be updated via the addRoznama function
            // We don't need to call refresh() here as it will cause a re-render loop
            // Instead, update the local state with the new data
            setSelectedRoznama(createResult.data);
            setOpeningBalance({
              cash: createResult.data.openingBalanceCash || 0,
              bank: createResult.data.openingBalanceBank || 0,
              fine: createResult.data.openingBalanceFine || 0,
            });
            setManualTransactions({
              income: createResult.data.incomeTransactions || [],
              expense: createResult.data.expenseTransactions || [],
            });
          }
        } else {
          console.log("No previous roznama found, showing empty state");
          setSelectedRoznama(null);
          setOpeningBalance({ cash: 0, bank: 0, fine: 0 });
          setManualTransactions({ income: [], expense: [] });
        }
      }
    } catch (error) {
      console.error("Error loading roznama:", error);
      setSelectedRoznama(null);
    } finally {
      setLoadingRoznama(false);
      setIsInitialLoad(false);
    }
  }, [roznama, addRoznama, loadingRoznama, loading]);

  useEffect(() => {
    // 🔥 CRITICAL FIX: Wait for initial data loading to complete
    if (loading) {
      console.log("Data still loading, waiting...");
      return;
    }

    const targetDate = convertDateToYYYYMMDD(selectedDate);
    console.log(`useEffect: Checking for roznama on ${targetDate}, loading: ${loading}`);

    // Check if we already have a roznama for this date in the context
    const existingRoznama = roznama?.find(r => convertDateToYYYYMMDD(r.date) === targetDate);

    if (existingRoznama) {
      // 🔥 FIX: Always update when the data changes, not just when ID differs
      // Check if the data has actually changed by comparing transaction counts or a hash
      const currentIncomeCount = selectedRoznama?.incomeTransactions?.length || 0;
      const newIncomeCount = existingRoznama.incomeTransactions?.length || 0;
      const currentExpenseCount = selectedRoznama?.expenseTransactions?.length || 0;
      const newExpenseCount = existingRoznama.expenseTransactions?.length || 0;

      const hasChanged =
        currentIncomeCount !== newIncomeCount ||
        currentExpenseCount !== newExpenseCount ||
        selectedRoznama?.id !== existingRoznama.id ||
        selectedRoznama?.updated_at !== existingRoznama.updated_at;

      if (hasChanged) {
        console.log(`Found existing roznama for ${targetDate} with ID: ${existingRoznama.id}, updating state`);
        setSelectedRoznama(existingRoznama);
        setOpeningBalance({
          cash: existingRoznama.openingBalanceCash || 0,
          bank: existingRoznama.openingBalanceBank || 0,
          fine: existingRoznama.openingBalanceFine || 0,
        });
        setManualTransactions({
          income: existingRoznama.incomeTransactions || [],
          expense: existingRoznama.expenseTransactions || [],
        });
      }
      setIsInitialLoad(false);
      return;
    }

    // Only try to load if we're not already loading and data is available
    if (!loadingRoznama && !isInitialLoad) {
      console.log(`No roznama found for ${targetDate}, loading...`);
      loadRoznamaForDate(selectedDate);
    } else if (isInitialLoad && !loading) {
      console.log(`Initial load for ${targetDate}...`);
      loadRoznamaForDate(selectedDate);
    }
  }, [selectedDate, roznama, loadRoznamaForDate, loadingRoznama, isInitialLoad, selectedRoznama?.id, loading, selectedRoznama?.updated_at, selectedRoznama?.incomeTransactions?.length, selectedRoznama?.expenseTransactions?.length]);

  // Add this useEffect after the existing ones
  useEffect(() => {
    // 🔥 FIX: Force update when roznama context changes
    if (!loading && !loadingRoznama) {
      const targetDate = convertDateToYYYYMMDD(selectedDate);
      const existingRoznama = roznama?.find(r => convertDateToYYYYMMDD(r.date) === targetDate);

      if (existingRoznama) {
        // Check if the data has actually changed
        const currentIncomeCount = selectedRoznama?.incomeTransactions?.length || 0;
        const newIncomeCount = existingRoznama.incomeTransactions?.length || 0;
        const currentExpenseCount = selectedRoznama?.expenseTransactions?.length || 0;
        const newExpenseCount = existingRoznama.expenseTransactions?.length || 0;

        if (currentIncomeCount !== newIncomeCount ||
          currentExpenseCount !== newExpenseCount ||
          selectedRoznama?.updated_at !== existingRoznama.updated_at) {
          console.log("Context roznama updated, refreshing selected roznama");
          setSelectedRoznama(existingRoznama);
          setOpeningBalance({
            cash: existingRoznama.openingBalanceCash || 0,
            bank: existingRoznama.openingBalanceBank || 0,
            fine: existingRoznama.openingBalanceFine || 0,
          });
          setManualTransactions({
            income: existingRoznama.incomeTransactions || [],
            expense: existingRoznama.expenseTransactions || [],
          });
        }
      }
    }
  }, [roznama, selectedDate, loading, loadingRoznama, selectedRoznama?.updated_at, selectedRoznama?.incomeTransactions?.length, selectedRoznama?.expenseTransactions?.length]);

  useEffect(() => {
    if (selectedRoznama) {
      setOpeningBalance({
        cash: selectedRoznama.openingBalanceCash || 0,
        bank: selectedRoznama.openingBalanceBank || 0,
        fine: selectedRoznama.openingBalanceFine || 0,
      });
      setManualTransactions({
        income: selectedRoznama.incomeTransactions || [],
        expense: selectedRoznama.expenseTransactions || [],
      });
    } else {
      const today = format(new Date(), "yyyy-MM-dd");
      if (selectedDate !== today) {
        setOpeningBalance({ cash: 0, bank: 0, fine: 0 });
        setManualTransactions({ income: [], expense: [] });
      }
    }
  }, [selectedRoznama, selectedDate]);

  // ============ AUTO TRANSACTIONS ============
  const autoIncomeTransactions = useMemo(() => [], []);
  const autoExpenseTransactions = useMemo(() => [], []);

  // ============ COMBINED TRANSACTIONS ============
  const allIncomeTransactions = useMemo(() => {
    if (isDayClosed() || isPastDay()) return [...manualTransactions.income];
    return [...manualTransactions.income, ...autoIncomeTransactions];
  }, [manualTransactions.income, autoIncomeTransactions, isDayClosed, isPastDay]);

  const allExpenseTransactions = useMemo(() => {
    if (isDayClosed() || isPastDay()) return [...manualTransactions.expense];
    return [...manualTransactions.expense, ...autoExpenseTransactions];
  }, [manualTransactions.expense, autoExpenseTransactions, isDayClosed, isPastDay]);

  // ============ SUMMARY ============
  const summary = useMemo(() => {
    let cashIncome = 0, bankIncome = 0, cashExpense = 0, bankExpense = 0;
    // ✅ Removed unused fineReceived and finePaid variables (they're only used for calculation)

    allIncomeTransactions.forEach(t => {
      cashIncome += parseFloat(t.cash) || 0;
      bankIncome += parseFloat(t.bank) || 0;
    });

    allExpenseTransactions.forEach(t => {
      cashExpense += parseFloat(t.cash) || 0;
      bankExpense += parseFloat(t.bank) || 0;
    });

    const totalIncome = cashIncome + bankIncome;
    const totalExpense = cashExpense + bankExpense;
    const netProfit = totalIncome - totalExpense;

    // Calculate fine totals separately
    const fineReceived = allIncomeTransactions.reduce((sum, t) => sum + (parseFloat(t.fineReceived) || 0), 0);
    const finePaid = allExpenseTransactions.reduce((sum, t) => sum + (parseFloat(t.finePaid) || 0), 0);

    return {
      openingBalanceCash: openingBalance.cash || 0,
      openingBalanceBank: openingBalance.bank || 0,
      openingBalanceFine: openingBalance.fine || 0,
      cashIncome,
      bankIncome,
      cashExpense,
      bankExpense,
      totalIncome,
      totalExpense,
      netProfit,
      fineReceived,
      finePaid,
      closingBalanceCash: (openingBalance.cash || 0) + cashIncome - cashExpense,
      closingBalanceBank: (openingBalance.bank || 0) + bankIncome - bankExpense,
      closingBalanceFine: (openingBalance.fine || 0) + fineReceived - finePaid,
      totalClosingBalance: ((openingBalance.cash || 0) + cashIncome - cashExpense) + ((openingBalance.bank || 0) + bankIncome - bankExpense),
    };
  }, [openingBalance, allIncomeTransactions, allExpenseTransactions]);

  // ============ SEARCH HANDLERS ============
  const handleCustomerSearch = useCallback((query) => {
    setCustomerQuery(query);

    if (!query.trim()) {
      setCustomerSuggestions([]);
      setCustomerPopperOpen(false);
      return;
    }

    const filtered = customers.filter(c =>
      c.name?.toLowerCase()?.includes(query.toLowerCase()) ||
      c.contact?.toString()?.includes(query) ||
      c.customerShortcut?.toLowerCase()?.includes(query.toLowerCase())
    );
    setCustomerSuggestions(filtered);
    setCustomerPopperOpen(filtered.length > 0);
  }, [customers]);

  const handleSupplierSearch = useCallback((query) => {
    setSupplierQuery(query);

    if (!query.trim()) {
      setSupplierSuggestions([]);
      setSupplierPopperOpen(false);
      return;
    }

    const filtered = suppliers.filter(s =>
      s.name?.toLowerCase()?.includes(query.toLowerCase()) ||
      s.contact?.toString()?.includes(query) ||
      s.supplierShortcut?.toLowerCase()?.includes(query.toLowerCase())
    );
    setSupplierSuggestions(filtered);
    setSupplierPopperOpen(filtered.length > 0);
  }, [suppliers]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery(customer.name);
    setCustomerSuggestions([]);
    setCustomerPopperOpen(false);
    setNewTransaction(prev => ({
      ...prev,
      partyType: "customer",
      customerId: customer.id,
      partyName: customer.name
    }));

    const balance = getCustomerBalance(customer.id);
    if (balance) {
      const parts = [];
      if (parseFloat(balance.cashBalance) > 0) parts.push(`Cash: ₹${balance.cashBalance}`);
      if (parseFloat(balance.bankBalance) > 0) parts.push(`Bank: ₹${balance.bankBalance}`);
      if (parseFloat(balance.fineBalance) > 0) parts.push(`Fine: ${balance.fineBalance} gm`);
      if (parts.length > 0) {
        showSnackbar(`Customer: ${parts.join(', ')}`, "info");
      } else {
        showSnackbar(`Customer ${customer.name} - All cleared`, "success");
      }
    }
  };

  const handleSelectSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierQuery(supplier.name);
    setSupplierSuggestions([]);
    setSupplierPopperOpen(false);
    setNewTransaction(prev => ({
      ...prev,
      partyType: "supplier",
      supplierId: supplier.id,
      partyName: supplier.name
    }));

    const balance = getSupplierBalance(supplier.id);
    if (balance) {
      const parts = [];
      if (parseFloat(balance.cashBalance) > 0) parts.push(`Cash: ₹${balance.cashBalance}`);
      if (parseFloat(balance.bankBalance) > 0) parts.push(`Bank: ₹${balance.bankBalance}`);
      if (parseFloat(balance.fineBalance) > 0) parts.push(`Fine: ${balance.fineBalance} gm`);
      if (parts.length > 0) {
        showSnackbar(`Supplier: ${parts.join(', ')}`, "info");
      } else {
        showSnackbar(`Supplier ${supplier.name} - All cleared`, "success");
      }
    }
  };

  const clearSelection = () => {
    setSelectedCustomer(null);
    setSelectedSupplier(null);
    setCustomerQuery("");
    setSupplierQuery("");
    setCustomerSuggestions([]);
    setSupplierSuggestions([]);
    setCustomerPopperOpen(false);
    setSupplierPopperOpen(false);
    setNewTransaction(prev => ({
      ...prev,
      partyType: "manual",
      customerId: null,
      supplierId: null,
      partyName: ""
    }));
  };

  const [transactionDate, setTransactionDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // ============ DIALOG HANDLERS ============
  // Around line 574, update handleOpenDialog:
  const handleOpenDialog = (type = "income") => {
    // const prefix = type === "income" ? "INV" : "EXP";
    // const count = type === "income" ? manualTransactions.income.length : manualTransactions.expense.length;
    const billNumber = generateUniqueBillNumber(type);

    const defaultPartyType = type === "income" ? "customer" : "supplier";

    setNewTransaction({
      type,
      billNumber,
      description: "",
      amount: "",
      paymentMode: "cash",
      cashPortion: "",
      bankPortion: "",
      fineReceived: "",
      finePaid: "",
      partyName: "",
      partyType: defaultPartyType,
      customerId: null,
      supplierId: null,
      // 🔥 Reset conversion fields
      conversionType: "none",
      conversionAmount: "",
      conversionNote: "",
    });

    setTransactionDate(selectedDate);
    clearSelection();
    setEditingTransaction(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTransaction(null);
    clearSelection();
  };

  const handleViewTransaction = (transaction) => {
    setViewingTransaction(transaction);
    setViewDialogOpen(true);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);

    // 🔥 FIX: Set the transaction date when editing
    setTransactionDate(transaction.date || format(new Date(), "yyyy-MM-dd"));

    setNewTransaction({
      type: transaction.type,
      billNumber: transaction.billNumber,
      description: transaction.description || "",
      amount: transaction.amount || "",
      paymentMode: transaction.paymentMode || "cash",
      cashPortion: transaction.cash || "",
      bankPortion: transaction.bank || "",
      fineReceived: transaction.fineReceived || "",
      finePaid: transaction.finePaid || "",
      partyName: transaction.partyName || "",
      partyType: transaction.partyType || "manual",
      customerId: transaction.customerId || null,
      supplierId: transaction.supplierId || null,
      conversionType: transaction.conversionType || "none",
      conversionAmount: transaction.conversionAmount || "",
      conversionNote: transaction.conversionNote || "",
    });

    if (transaction.partyType === "customer" && transaction.customerId) {
      const customer = customers.find(c => c.id === transaction.customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setCustomerQuery(customer.name);
      }
    } else if (transaction.partyType === "supplier" && transaction.supplierId) {
      const supplier = suppliers.find(s => s.id === transaction.supplierId);
      if (supplier) {
        setSelectedSupplier(supplier);
        setSupplierQuery(supplier.name);
      }
    }

    setOpenDialog(true);
  };

  const handleDeleteTransaction = async (transaction) => {
    if (!window.confirm("Delete this transaction?")) return;

    try {
      let updatedManualTransactions;
      if (transaction.type === "income") {
        updatedManualTransactions = {
          ...manualTransactions,
          income: manualTransactions.income.filter(t => t.id !== transaction.id),
        };
      } else {
        updatedManualTransactions = {
          ...manualTransactions,
          expense: manualTransactions.expense.filter(t => t.id !== transaction.id),
        };
      }
      setManualTransactions(updatedManualTransactions);

      // 🔥 FIX: Find the correct roznama for the transaction's date
      const targetDate = convertDateToYYYYMMDD(transaction.date);
      const targetRoznama = roznama?.find(r => convertDateToYYYYMMDD(r.date) === targetDate);

      if (targetRoznama?.id) {
        const allIncome = [...updatedManualTransactions.income, ...autoIncomeTransactions];
        const allExpense = [...updatedManualTransactions.expense, ...autoExpenseTransactions];

        let cashIncome = 0, bankIncome = 0, cashExpense = 0, bankExpense = 0;
        let fineReceived = 0, finePaid = 0;
        allIncome.forEach(t => { cashIncome += parseFloat(t.cash) || 0; bankIncome += parseFloat(t.bank) || 0; fineReceived += parseFloat(t.fineReceived) || 0; });
        allExpense.forEach(t => { cashExpense += parseFloat(t.cash) || 0; bankExpense += parseFloat(t.bank) || 0; finePaid += parseFloat(t.finePaid) || 0; });

        const updateData = {
          date: transaction.date,
          openingBalanceCash: targetRoznama.openingBalanceCash || 0,
          openingBalanceBank: targetRoznama.openingBalanceBank || 0,
          openingBalanceFine: targetRoznama.openingBalanceFine || 0,
          closingBalanceCash: (targetRoznama.openingBalanceCash || 0) + cashIncome - cashExpense,
          closingBalanceBank: (targetRoznama.openingBalanceBank || 0) + bankIncome - bankExpense,
          closingBalanceFine: (targetRoznama.openingBalanceFine || 0) + fineReceived - finePaid,
          incomeTransactions: allIncome,
          expenseTransactions: allExpense,
          netProfit: (cashIncome + bankIncome) - (cashExpense + bankExpense),
          totalIncome: cashIncome + bankIncome,
          totalExpense: cashExpense + bankExpense,
        };

        const response = await updateRoznama(targetRoznama.id, updateData);
        if (response.success) {
          showSnackbar("Transaction deleted!", "success");
          await refresh();
          await loadRoznamaForDate(selectedDate);
        } else {
          showSnackbar(`Error: ${response.error}`, "error");
          setManualTransactions(manualTransactions);
        }
      } else {
        // If no roznama found, just update local state
        showSnackbar("Transaction deleted locally!", "success");
      }
    } catch (error) {
      showSnackbar(`Error: ${error.message}`, "error");
      setManualTransactions(manualTransactions);
    }
  };

  // ============ SUBMIT TRANSACTION ============
  const handleSubmitTransaction = async () => {
    console.log(newTransaction)
    const cashAmount = parseFloat(newTransaction.cashPortion) || 0;
    const bankAmount = parseFloat(newTransaction.bankPortion) || 0;
    const conversionAmount = parseFloat(newTransaction.conversionAmount) || 0;
    const conversionType = newTransaction.conversionType || "none";
    const fineAmount = parseFloat(newTransaction.type === 'income' ? conversionType === "fineForCash" ? conversionAmount : newTransaction.fineReceived : newTransaction.finePaid) || 0;
    console.log(fineAmount, conversionType)
    const rate = metalRate || 15000;

    let roznamaCash = cashAmount;
    let roznamaBank = bankAmount;
    let roznamaFine = 0;
    let customerCash = cashAmount;
    let customerBank = bankAmount;
    let customerFine = fineAmount.toFixed(3);
    let conversionDescription = "";

    if (conversionType === "cashForFine" && conversionAmount > 0 && rate > 0) {
      const fineEquivalent = conversionAmount / rate;
      conversionDescription = ` (💰 ₹${conversionAmount.toLocaleString()} in lieu of 💎 ${fineEquivalent.toFixed(3)}gm fine @ ₹${rate}/gm)`;

      if (newTransaction.type === "income") {
        roznamaCash += conversionAmount;
        customerFine += fineEquivalent;
      } else {
        roznamaCash += conversionAmount;
        customerFine += fineEquivalent;
      }
    }
    else if (conversionType === "fineForCash" && conversionAmount > 0 && rate > 0) {
      const cashEquivalent = conversionAmount * rate;

      if (newTransaction.type === "income") {
        roznamaFine += conversionAmount;
        customerCash += cashEquivalent;
        conversionDescription = ` (💎 ${conversionAmount}gm in lieu of 💰 ₹${cashEquivalent.toLocaleString()} cash @ ₹${rate}/gm)`;
      } else {
        roznamaFine += conversionAmount;
        customerCash += cashEquivalent;
        conversionDescription = ` (💎 ${conversionAmount}gm in lieu of 💰 ₹${cashEquivalent.toLocaleString()} cash @ ₹${rate}/gm)`;
      }
    }

    const totalAmount = roznamaCash + roznamaBank;

    if (totalAmount <= 0 && roznamaFine <= 0 && customerFine <= 0) {
      showSnackbar("Please enter either an amount (cash/bank) or fine", "warning");
      return;
    }

    const transactionAmount = totalAmount > 0 ? totalAmount : 0;

    const transaction = {
      id: editingTransaction?.id || `manual-${Date.now()}`,
      date: transactionDate,
      type: newTransaction.type,
      billNumber: newTransaction.billNumber,
      description: newTransaction.description + (conversionDescription || ""),
      amount: transactionAmount,
      cash: roznamaCash,
      bank: roznamaBank,
      paymentMode: roznamaCash > 0 && roznamaBank > 0 ? "split" :
        roznamaCash > 0 ? "cash" :
          roznamaBank > 0 ? "bank" : "fine",
      fineReceived: newTransaction.type === "income" ? conversionType === "cashForFine" ? 0 : customerFine : 0,
      finePaid: newTransaction.type === "expense" ? conversionType === "cashForFine" ? 0 : conversionType === "fineForCash" ? conversionAmount : customerFine : 0,
      customerCashAmount: customerCash,
      customerBankAmount: customerBank,
      customerFineAmount: customerFine,
      conversionType: conversionType !== "none" ? conversionType : undefined,
      conversionAmount: conversionAmount > 0 ? conversionAmount : undefined,
      metalRate: conversionType !== "none" ? rate : undefined,
      conversionNote: conversionDescription,
      partyName: newTransaction.partyName || "Manual Entry",
      isNewTransaction: true,
      partyType: newTransaction.partyType || "manual",
      customerId: newTransaction.customerId,
      supplierId: newTransaction.supplierId,
      isAutoGenerated: false,
      created_at: editingTransaction?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      isBackdated: transactionDate !== format(new Date(), "yyyy-MM-dd"),
      originalEntryDate: format(new Date(), "yyyy-MM-dd"),
    };
    console.log(transaction, customerFine)

    // Update local state first
    let updatedManualTransactions;
    if (editingTransaction) {
      if (newTransaction.type === "income") {
        updatedManualTransactions = {
          ...manualTransactions,
          income: manualTransactions.income.map(t => t.id === editingTransaction.id ? transaction : t),
        };
      } else {
        updatedManualTransactions = {
          ...manualTransactions,
          expense: manualTransactions.expense.map(t => t.id === editingTransaction.id ? transaction : t),
        };
      }
    } else {
      if (newTransaction.type === "income") {
        updatedManualTransactions = {
          ...manualTransactions,
          income: [...manualTransactions.income, transaction],
        };
      } else {
        updatedManualTransactions = {
          ...manualTransactions,
          expense: [...manualTransactions.expense, transaction],
        };
      }
    }

    setManualTransactions(updatedManualTransactions);

    try {
      const targetDate = convertDateToYYYYMMDD(transactionDate);

      let targetRoznama = roznama?.find(r => convertDateToYYYYMMDD(r.date) === targetDate);

      if (!targetRoznama) {
        const sortedRoznama = [...roznama].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB;
        });

        let previousRoznama = null;
        for (let i = sortedRoznama.length - 1; i >= 0; i--) {
          const rDate = new Date(sortedRoznama[i].date);
          const tDate = new Date(targetDate);
          if (rDate < tDate) {
            previousRoznama = sortedRoznama[i];
            break;
          }
        }

        const openingBalances = previousRoznama ? {
          cash: previousRoznama.closingBalanceCash || 0,
          bank: previousRoznama.closingBalanceBank || 0,
          fine: previousRoznama.closingBalanceFine || 0,
        } : { cash: 0, bank: 0, fine: 0 };

        const newRoznamaData = {
          date: targetDate,
          openingBalanceCash: openingBalances.cash,
          openingBalanceBank: openingBalances.bank,
          openingBalanceFine: openingBalances.fine,
          closingBalanceCash: openingBalances.cash,
          closingBalanceBank: openingBalances.bank,
          closingBalanceFine: openingBalances.fine,
          incomeTransactions: [],
          expenseTransactions: [],
          netProfit: 0,
          totalIncome: 0,
          totalExpense: 0,
          isBackdated: targetDate !== format(new Date(), "yyyy-MM-dd"),
          originalEntryDate: format(new Date(), "yyyy-MM-dd"),
        };

        const createResponse = await addRoznama(newRoznamaData);
        if (createResponse.success) {
          await refresh();
          const updatedRoznama = roznama?.find(r => convertDateToYYYYMMDD(r.date) === targetDate);
          if (updatedRoznama) {
            targetRoznama = updatedRoznama;
          }
        } else {
          throw new Error("Failed to create roznama for the selected date");
        }
      }

      if (!targetRoznama) {
        throw new Error("Could not find or create roznama for the selected date");
      }

      const existingIncome = targetRoznama.incomeTransactions || [];
      const existingExpense = targetRoznama.expenseTransactions || [];

      let finalIncome = [...existingIncome];
      let finalExpense = [...existingExpense];

      if (editingTransaction) {
        const oldTransactionId = editingTransaction.id;
        const inIncome = existingIncome.some(t => t.id === oldTransactionId);
        const inExpense = existingExpense.some(t => t.id === oldTransactionId);

        if (inIncome) {
          finalIncome = existingIncome.filter(t => t.id !== oldTransactionId);
        } else if (inExpense) {
          finalExpense = existingExpense.filter(t => t.id !== oldTransactionId);
        }
      }

      if (newTransaction.type === "income") {
        const existsIndex = finalIncome.findIndex(t => t.id === transaction.id);
        if (existsIndex >= 0) {
          finalIncome[existsIndex] = transaction;
        } else {
          finalIncome.push(transaction);
        }
      } else {
        const existsIndex = finalExpense.findIndex(t => t.id === transaction.id);
        if (existsIndex >= 0) {
          finalExpense[existsIndex] = transaction;
        } else {
          finalExpense.push(transaction);
        }
      }

      let cashIncome = 0, bankIncome = 0, cashExpense = 0, bankExpense = 0;
      let fineReceived = 0, finePaid = 0;

      finalIncome.forEach(t => {
        cashIncome += parseFloat(t.cash) || 0;
        bankIncome += parseFloat(t.bank) || 0;
        fineReceived += parseFloat(t.fineReceived) || 0;
      });

      finalExpense.forEach(t => {
        cashExpense += parseFloat(t.cash) || 0;
        bankExpense += parseFloat(t.bank) || 0;
        finePaid += parseFloat(t.finePaid) || 0;
      });

      const totalIncome = cashIncome + bankIncome;
      const totalExpense = cashExpense + bankExpense;
      const netProfit = totalIncome - totalExpense;

      const openingBalances = {
        cash: targetRoznama.openingBalanceCash || 0,
        bank: targetRoznama.openingBalanceBank || 0,
        fine: targetRoznama.openingBalanceFine || 0,
      };

      const updateData = {
        date: targetDate,
        openingBalanceCash: openingBalances.cash,
        openingBalanceBank: openingBalances.bank,
        openingBalanceFine: openingBalances.fine,
        closingBalanceCash: openingBalances.cash + cashIncome - cashExpense,
        closingBalanceBank: openingBalances.bank + bankIncome - bankExpense,
        closingBalanceFine: openingBalances.fine + fineReceived - finePaid,
        incomeTransactions: finalIncome,
        expenseTransactions: finalExpense,
        netProfit: netProfit,
        totalIncome: totalIncome,
        totalExpense: totalExpense,
        isBackdated: targetDate !== format(new Date(), "yyyy-MM-dd"),
        originalEntryDate: format(new Date(), "yyyy-MM-dd"),
      };

      const response = await updateRoznama(targetRoznama.id, updateData);

      if (response.success) {
        showSnackbar(editingTransaction ? "Transaction updated!" : "Transaction added!", "success");

        // 🔥 CRITICAL FIX: Update the roznama in context immediately
        // Instead of waiting for refresh, manually update the selectedRoznama state
        // and the roznama context data

        // 1. Update the selectedRoznama state with the new data
        const updatedRoznamaData = {
          ...targetRoznama,
          ...updateData,
          incomeTransactions: finalIncome,
          expenseTransactions: finalExpense,
        };

        setSelectedRoznama(updatedRoznamaData);

        // 2. Also update the manualTransactions state to match
        // (already done above, but ensure it's the final data)
        setManualTransactions({
          income: finalIncome.filter(t => t.type === "income"),
          expense: finalExpense.filter(t => t.type === "expense"),
        });

        // 3. If backdated, recalculate subsequent roznamas
        if (targetDate !== format(new Date(), "yyyy-MM-dd")) {
          try {
            await recalculateRoznamas(targetDate);
          } catch (recalcError) {
            console.warn("Recalculation warning:", recalcError);
          }
        }

        // 4. Refresh to ensure everything is synced
        await refresh();

        // 5. Reload the roznama for the selected date to ensure it's up to date
        await loadRoznamaForDate(selectedDate);

        handleCloseDialog();
      } else {
        showSnackbar(`Error: ${response.error}`, "error");
        setManualTransactions(manualTransactions);
      }
    } catch (error) {
      console.error("Error submitting transaction:", error);
      showSnackbar(`Error: ${error.message}`, "error");
      setManualTransactions(manualTransactions);
    }
  };

  // ============ CLOSE DAY ============
  const handleCloseDayClick = () => {
    const hasTransactions = allIncomeTransactions.length > 0 || allExpenseTransactions.length > 0;
    if (!hasTransactions) {
      showSnackbar("No transactions to close. Add some transactions first.", "warning");
      return;
    }

    setDifferences({
      cash: summary.closingBalanceCash,
      bank: summary.closingBalanceBank,
      fine: summary.closingBalanceFine,
      total: summary.totalClosingBalance,
    });
    setActualBalances({
      cash: summary.closingBalanceCash,
      bank: summary.closingBalanceBank,
      fine: summary.closingBalanceFine,
    });
    setCloseDayDialogOpen(true);
  };

  const handleActualBalanceChange = (field, value) => {
    const newValue = parseFloat(value) || 0;
    setActualBalances(prev => ({ ...prev, [field]: newValue }));
    const cashDiff = field === 'cash' ? newValue - summary.closingBalanceCash : actualBalances.cash - summary.closingBalanceCash;
    const bankDiff = field === 'bank' ? newValue - summary.closingBalanceBank : actualBalances.bank - summary.closingBalanceBank;
    const fineDiff = field === 'fine' ? newValue - summary.closingBalanceFine : actualBalances.fine - summary.closingBalanceFine;
    setDifferences({ cash: cashDiff, bank: bankDiff, fine: fineDiff, total: cashDiff + bankDiff });
  };

  const handleConfirmCloseDay = async () => {
    setIsClosingDay(true);
    try {
      const hasDifference = differences.cash !== 0 || differences.bank !== 0 || differences.fine !== 0;
      let suspenseTransaction = null;
      let updatedManualTransactions = { ...manualTransactions };

      if (hasDifference) {
        suspenseTransaction = {
          id: `suspense-${Date.now()}`,
          date: selectedDate,
          type: differences.total > 0 ? "income" : "expense",
          billNumber: `SUSP-${format(new Date(), 'yyMMddHHmmss')}`,
          description: `Suspense adjustment for ${format(parseISO(selectedDate), 'dd/MM/yyyy')}`,
          amount: Math.abs(differences.total),
          paymentMode: "cash",
          cash: Math.abs(differences.cash),
          bank: Math.abs(differences.bank),
          fineReceived: Math.abs(differences.fine) || 0,
          finePaid: 0,
          partyName: "Suspense Adjustment",
          partyType: "suspense",
          isAutoGenerated: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        if (differences.total > 0) {
          updatedManualTransactions.income = [...updatedManualTransactions.income, suspenseTransaction];
        } else {
          updatedManualTransactions.expense = [...updatedManualTransactions.expense, suspenseTransaction];
        }
        setManualTransactions(updatedManualTransactions);
      }

      const allIncome = [...updatedManualTransactions.income, ...autoIncomeTransactions];
      const allExpense = [...updatedManualTransactions.expense, ...autoExpenseTransactions];

      // Calculate totals for the API call
      let cashIncome = 0, bankIncome = 0, cashExpense = 0, bankExpense = 0;

      allIncome.forEach(t => {
        cashIncome += parseFloat(t.cash) || 0;
        bankIncome += parseFloat(t.bank) || 0;
      });

      allExpense.forEach(t => {
        cashExpense += parseFloat(t.cash) || 0;
        bankExpense += parseFloat(t.bank) || 0;
      });

      // ✅ Remove unused fineReceived and finePaid variables
      // The API uses actualBalances.fine directly for closing fine balance

      const response = await closeRoznamaDay(
        selectedDate,
        openingBalance.cash,
        openingBalance.bank,
        openingBalance.fine,
        allIncome,
        allExpense,
        actualBalances.bank,
        actualBalances.cash,
        actualBalances.fine,
        (cashIncome + bankIncome) - (cashExpense + bankExpense),
        cashIncome + bankIncome,
        cashExpense + bankExpense
      );

      if (response.success) {
        showSnackbar(hasDifference ? "Day closed with suspense entry!" : "Day closed successfully!", "success");
        await refresh();
        const nextDay = format(addDays(parseISO(selectedDate), 1), "yyyy-MM-dd");
        setSelectedDate(nextDay);
        setManualTransactions({ income: [], expense: [] });
        setCloseDayDialogOpen(false);
      } else {
        showSnackbar(`Error: ${response.error || "Failed to close day"}`, "error");
      }
    } catch (error) {
      showSnackbar(`Error: ${error.message}`, "error");
    } finally {
      setIsClosingDay(false);
    }
  };

  // ============ SNACKBAR ============
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ============ EXPORT ============
  const handleExport = () => {
    let csv = "JEWELLERY DAYBOOK REPORT\n";
    csv += `Date: ${selectedDate}\n\n`;
    csv += "INCOME\n";
    csv += "Party,Description,Cash,Bank,Fine Received\n";
    allIncomeTransactions.forEach(t => {
      csv += `${t.partyName || ""},"${t.description || ""}",${t.cash || 0},${t.bank || 0},${t.fineReceived || 0}\n`;
    });
    csv += `\nTotal Income: ₹${summary.totalIncome.toLocaleString("en-IN")}\n\n`;
    csv += "EXPENSE\n";
    csv += "Party,Description,Cash,Bank,Fine Paid\n";
    allExpenseTransactions.forEach(t => {
      csv += `${t.partyName || ""},"${t.description || ""}",${t.cash || 0},${t.bank || 0},${t.finePaid || 0}\n`;
    });
    csv += `\nTotal Expense: ₹${summary.totalExpense.toLocaleString("en-IN")}\n\n`;
    csv += `Net Profit/Loss: ₹${summary.netProfit.toLocaleString("en-IN")}\n`;
    csv += `Closing Cash: ₹${summary.closingBalanceCash.toLocaleString("en-IN")}\n`;
    csv += `Closing Bank: ₹${summary.closingBalanceBank.toLocaleString("en-IN")}\n`;
    csv += `Closing Fine: ${summary.closingBalanceFine.toFixed(3)} gm\n`;

    const link = document.createElement("a");
    link.setAttribute("href", `data:text/csv;charset=utf-8,${encodeURI(csv)}`);
    link.setAttribute("download", `Daybook_${selectedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ============ UNIQUE BILL NUMBER GENERATOR ============
  const generateUniqueBillNumber = (type) => {
    const prefix = type === "income" ? "INV" : "EXP";
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${prefix}-${timestamp}-${random}`;
  };

  // ============ RENDER SEARCH POPPER ============
  const renderCustomerPopper = () => {
    if (!customerPopperOpen || customerSuggestions.length === 0) return null;

    return (
      <Popper
        open={customerPopperOpen}
        anchorEl={customerAnchorRef.current}
        placement="bottom-start"
        transition
        style={{ width: customerAnchorRef.current?.clientWidth || 300, zIndex: 1300 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <MuiPaper
              elevation={4}
              sx={{
                maxHeight: 300,
                overflow: 'auto',
                borderRadius: 2,
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                bgcolor: isDark ? '#1e293b' : '#ffffff',
              }}
            >
              <ClickAwayListener onClickAway={() => setCustomerPopperOpen(false)}>
                <List dense disablePadding>
                  {customerSuggestions.map((customer) => {
                    const balance = getCustomerBalance(customer.id);
                    return (
                      <ListItem
                        key={customer.id}
                        button
                        onClick={() => handleSelectCustomer(customer)}
                        sx={{
                          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' },
                          borderBottom: `1px solid ${isDark ? '#334155' : '#f1f5f9'}`,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: '#667eea', width: 36, height: 36 }}>
                            {customer.name?.charAt(0) || 'C'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {customer.name}
                              </Typography>
                              {customer.customerShortcut && (
                                <Chip label={customer.customerShortcut} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 12 }} /> {customer.contact || 'N/A'}
                              </Typography>
                              {balance && (
                                <>
                                  {parseFloat(balance.cashBalance) > 0 && (
                                    <Typography variant="caption" sx={{ color: '#16a34a' }}>
                                      Cash: ₹{balance.cashBalance}
                                    </Typography>
                                  )}
                                  {parseFloat(balance.bankBalance) > 0 && (
                                    <Typography variant="caption" sx={{ color: '#2563eb' }}>
                                      Bank: ₹{balance.bankBalance}
                                    </Typography>
                                  )}
                                  {parseFloat(balance.fineBalance) > 0 && (
                                    <Typography variant="caption" sx={{ color: '#f59e0b' }}>
                                      Fine: {balance.fineBalance} gm
                                    </Typography>
                                  )}
                                  {parseFloat(balance.cashBalance) === 0 &&
                                    parseFloat(balance.bankBalance) === 0 &&
                                    parseFloat(balance.fineBalance) === 0 && (
                                      <Typography variant="caption" sx={{ color: '#10b981' }}>
                                        ✅ All cleared
                                      </Typography>
                                    )}
                                </>
                              )}
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </ClickAwayListener>
            </MuiPaper>
          </Fade>
        )}
      </Popper>
    );
  };

  const renderSupplierPopper = () => {
    if (!supplierPopperOpen || supplierSuggestions.length === 0) return null;

    return (
      <Popper
        open={supplierPopperOpen}
        anchorEl={supplierAnchorRef.current}
        placement="bottom-start"
        transition
        style={{ width: supplierAnchorRef.current?.clientWidth || 300, zIndex: 1300 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <MuiPaper
              elevation={4}
              sx={{
                maxHeight: 300,
                overflow: 'auto',
                borderRadius: 2,
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                bgcolor: isDark ? '#1e293b' : '#ffffff',
              }}
            >
              <ClickAwayListener onClickAway={() => setSupplierPopperOpen(false)}>
                <List dense disablePadding>
                  {supplierSuggestions.map((supplier) => {
                    const balance = getSupplierBalance(supplier.id);
                    return (
                      <ListItem
                        key={supplier.id}
                        button
                        onClick={() => handleSelectSupplier(supplier)}
                        sx={{
                          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' },
                          borderBottom: `1px solid ${isDark ? '#334155' : '#f1f5f9'}`,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: '#f59e0b', width: 36, height: 36 }}>
                            {supplier.name?.charAt(0) || 'S'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {supplier.name}
                              </Typography>
                              {supplier.supplierShortcut && (
                                <Chip label={supplier.supplierShortcut} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 12 }} /> {supplier.contact || 'N/A'}
                              </Typography>
                              {balance && (
                                <>
                                  {parseFloat(balance.cashBalance) > 0 && (
                                    <Typography variant="caption" sx={{ color: '#16a34a' }}>
                                      Cash: ₹{balance.cashBalance}
                                    </Typography>
                                  )}
                                  {parseFloat(balance.bankBalance) > 0 && (
                                    <Typography variant="caption" sx={{ color: '#2563eb' }}>
                                      Bank: ₹{balance.bankBalance}
                                    </Typography>
                                  )}
                                  {parseFloat(balance.fineBalance) > 0 && (
                                    <Typography variant="caption" sx={{ color: '#f59e0b' }}>
                                      Fine: {balance.fineBalance} gm
                                    </Typography>
                                  )}
                                  {parseFloat(balance.cashBalance) === 0 &&
                                    parseFloat(balance.bankBalance) === 0 &&
                                    parseFloat(balance.fineBalance) === 0 && (
                                      <Typography variant="caption" sx={{ color: '#10b981' }}>
                                        ✅ All cleared
                                      </Typography>
                                    )}
                                </>
                              )}
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </ClickAwayListener>
            </MuiPaper>
          </Fade>
        )}
      </Popper>
    );
  };

  // ============ RENDER ============
  // 🔥 FIX: Wait for loading to complete before rendering
  if (loading && isInitialLoad) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // 🔥 FIX: If still loading but not initial, show loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // 🔥 FIX: Wait for roznama loading if it's initial
  if (loadingRoznama && isInitialLoad) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const isReadOnly = isPastDay() || isDayClosed();

  // ============ SELECTED PARTY BALANCE DISPLAY ============
  const renderSelectedPartyBalance = () => {
    if (selectedCustomer) {
      const balance = getCustomerBalance(selectedCustomer.id);
      if (!balance) return null;
      const hasBalance = parseFloat(balance.cashBalance) > 0 ||
        parseFloat(balance.bankBalance) > 0 ||
        parseFloat(balance.fineBalance) > 0;
      return (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <PersonIcon sx={{ fontSize: 16, color: "#667eea" }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCustomer.name}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {parseFloat(balance.cashBalance) > 0 && (
              <Chip
                icon={<LocalAtmIcon sx={{ fontSize: 14 }} />}
                label={`Cash: ₹${balance.cashBalance}`}
                size="small"
                sx={{ bgcolor: "rgba(22,163,74,0.1)", color: "#16a34a" }}
              />
            )}
            {parseFloat(balance.bankBalance) > 0 && (
              <Chip
                icon={<AccountBalanceIcon sx={{ fontSize: 14 }} />}
                label={`Bank: ₹${balance.bankBalance}`}
                size="small"
                sx={{ bgcolor: "rgba(37,99,235,0.1)", color: "#2563eb" }}
              />
            )}
            {parseFloat(balance.fineBalance) > 0 && (
              <Chip
                icon={<DiamondIcon sx={{ fontSize: 14 }} />}
                label={`Fine Due: ${balance.fineBalance} gm`}
                size="small"
                sx={{ bgcolor: "rgba(245,158,11,0.1)", color: "#f59e0b" }}
              />
            )}
            {!hasBalance && (
              <Chip
                label="✅ All Cleared"
                size="small"
                sx={{ bgcolor: "rgba(16,185,129,0.15)", color: "#10b981", fontWeight: 600 }}
              />
            )}
          </Box>
        </Box>
      );
    }

    if (selectedSupplier) {
      const balance = getSupplierBalance(selectedSupplier.id);
      if (!balance) return null;
      const hasBalance = parseFloat(balance.cashBalance) > 0 ||
        parseFloat(balance.bankBalance) > 0 ||
        parseFloat(balance.fineBalance) > 0;
      return (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <BusinessIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedSupplier.name}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {parseFloat(balance.cashBalance) > 0 && (
              <Chip
                icon={<LocalAtmIcon sx={{ fontSize: 14 }} />}
                label={`Cash: ₹${balance.cashBalance}`}
                size="small"
                sx={{ bgcolor: "rgba(22,163,74,0.1)", color: "#16a34a" }}
              />
            )}
            {parseFloat(balance.bankBalance) > 0 && (
              <Chip
                icon={<AccountBalanceIcon sx={{ fontSize: 14 }} />}
                label={`Bank: ₹${balance.bankBalance}`}
                size="small"
                sx={{ bgcolor: "rgba(37,99,235,0.1)", color: "#2563eb" }}
              />
            )}
            {parseFloat(balance.fineBalance) > 0 && (
              <Chip
                icon={<DiamondIcon sx={{ fontSize: 14 }} />}
                label={`Fine Due: ${balance.fineBalance} gm`}
                size="small"
                sx={{ bgcolor: "rgba(245,158,11,0.1)", color: "#f59e0b" }}
              />
            )}
            {!hasBalance && (
              <Chip
                label="✅ All Cleared"
                size="small"
                sx={{ bgcolor: "rgba(16,185,129,0.15)", color: "#10b981", fontWeight: 600 }}
              />
            )}
          </Box>
        </Box>
      );
    }
    return null;
  };

  // ============ HANDLE AMOUNT AUTO-CALCULATION ============
  const handleCashPortionChange = (e) => {
    const cashVal = parseFloat(e.target.value) || 0;
    const bankVal = parseFloat(newTransaction.bankPortion) || 0;
    const total = cashVal + bankVal;
    setNewTransaction(prev => ({
      ...prev,
      cashPortion: cashVal,
      amount: total.toString(),
      paymentMode: cashVal > 0 && bankVal > 0 ? "split" : cashVal > 0 ? "cash" : "bank"
    }));
  };

  const handleBankPortionChange = (e) => {
    const bankVal = parseFloat(e.target.value) || 0;
    const cashVal = parseFloat(newTransaction.cashPortion) || 0;
    const total = cashVal + bankVal;
    setNewTransaction(prev => ({
      ...prev,
      bankPortion: bankVal,
      amount: total.toString(),
      paymentMode: cashVal > 0 && bankVal > 0 ? "split" : cashVal > 0 ? "cash" : "bank"
    }));
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh", bgcolor: isDark ? "#0f172a" : "#f1f5f9" }}>
      <Container maxWidth="xl">

        {/* ============ HEADER ============ */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: isDark ? "#1e293b" : "#ffffff" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "primary.main" }}>
                <DiamondIcon sx={{ color: "white" }} />
              </Box>
              {/* In the header section, after the date display */}
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? "#f1f5f9" : "#1e293b" }}>
                  Jewellery Daybook
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                  {format(parseISO(selectedDate), "EEEE, dd MMMM yyyy")}
                  {isDayClosed() && <Chip label="CLOSED" size="small" color="warning" sx={{ ml: 1 }} />}
                  {selectedDate !== format(new Date(), "yyyy-MM-dd") && (
                    <Chip
                      label={`Viewing Backdate: ${format(parseISO(selectedDate), "dd/MM/yyyy")}`}
                      size="small"
                      color="info"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems={"center"}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>
                  Metal Rate:
                </Typography>
                <TextField
                  type="number"
                  value={metalRate}
                  onChange={(e) => setMetalRate(parseFloat(e.target.value) || 0)}
                  size="small"
                  sx={{ width: 120 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/gm</InputAdornment>,
                  }}
                />
              </Box>
              <TextField
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                size="small"
                sx={{ width: 160 }}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={async () => { await refresh(); loadRoznamaForDate(selectedDate); }} size="small">
                Refresh
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} size="small">
                Export
              </Button>
              <>
                <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={() => handleOpenDialog("income")} size="small">
                  Income
                </Button>
                <Button variant="contained" color="error" startIcon={<AddIcon />} onClick={() => handleOpenDialog("expense")} size="small">
                  Expense
                </Button>
              </>
              {!isReadOnly && (
                <Button
                  variant="contained"
                  startIcon={<LockIcon />}
                  onClick={handleCloseDayClick}
                  size="small"
                  disabled={isClosingDay || (allIncomeTransactions.length === 0 && allExpenseTransactions.length === 0)}
                >
                  Close Day
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={async () => {
                  if (window.confirm("This will recalculate all roznamas from the selected date. Continue?")) {
                    try {
                      const result = await recalculateRoznamas(selectedDate);
                      if (result.success) {
                        showSnackbar("Roznamas recalculated successfully!", "success");
                        await refresh();
                        loadRoznamaForDate(selectedDate);
                      } else {
                        showSnackbar(`Error: ${result.error || "Failed to recalculate"}`, "error");
                      }
                    } catch (error) {
                      showSnackbar(`Error: ${error.message}`, "error");
                    }
                  }
                }}
                size="small"
              >
                Recalculate
              </Button>
            </Stack>
          </Box>
        </Paper>

        {/* ============ OPENING BALANCE - Added Fine ============ */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: isDark ? "#1e293b" : "#ffffff" }}>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>Opening Cash</Typography>
              <TextField
                type="number"
                value={openingBalance.cash.toFixed(2)}
                onChange={(e) => setOpeningBalance(prev => ({ ...prev, cash: parseFloat(e.target.value) || 0 }))}
                size="small"
                disabled={isReadOnly}
                sx={{ width: 140 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>Opening Bank</Typography>
              <TextField
                type="number"
                value={openingBalance.bank.toFixed(2)}
                onChange={(e) => setOpeningBalance(prev => ({ ...prev, bank: parseFloat(e.target.value) || 0 }))}
                size="small"
                disabled={isReadOnly}
                sx={{ width: 140 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>Opening Fine</Typography>
              <TextField
                type="number"
                value={openingBalance.fine.toFixed(3)}
                onChange={(e) => setOpeningBalance(prev => ({ ...prev, fine: parseFloat(e.target.value) || 0 }))}
                size="small"
                disabled={isReadOnly}
                sx={{ width: 140 }}
                InputProps={{ startAdornment: <InputAdornment position="start">gm</InputAdornment> }}
              />
            </Box>
          </Box>
        </Paper>

        {/* ============ LEDGER STYLE TABLE ============ */}
        <Paper sx={{ borderRadius: 3, overflow: "hidden", bgcolor: isDark ? "#1e293b" : "#ffffff" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: `2px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
            <Box sx={{ p: 2, bgcolor: isDark ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.05)", borderRight: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#10b981", display: "flex", alignItems: "center", gap: 1 }}>
                <TrendingUpIcon /> Income
                <Chip label={`${allIncomeTransactions.length}`} size="small" sx={{ ml: 1, bgcolor: "rgba(16,185,129,0.2)", color: "#10b981" }} />
              </Typography>
            </Box>
            <Box sx={{ p: 2, bgcolor: isDark ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.05)" }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#ef4444", display: "flex", alignItems: "center", gap: 1 }}>
                <TrendingDownIcon /> Expense
                <Chip label={`${allExpenseTransactions.length}`} size="small" sx={{ ml: 1, bgcolor: "rgba(239,68,68,0.2)", color: "#ef4444" }} />
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            {/* Income Column */}
            <Box sx={{ borderRight: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`, minHeight: 400 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Party</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Cash</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Bank</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Fine</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allIncomeTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: isDark ? "#64748b" : "#94a3b8" }}>
                          No income transactions
                        </TableCell>
                      </TableRow>
                    ) : (
                      allIncomeTransactions.map((t, idx) => (
                        <TableRow key={t.id || idx} hover>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: "0.6rem", bgcolor: "rgba(16,185,129,0.2)", color: "#10b981" }}>
                                {t.partyName?.charAt(0) || "?"}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>{t.partyName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                              {t.description?.slice(0, 30) || "-"}
                            </Typography>
                            {t.isBackdated && (
                              <Chip
                                label={`Backdated: ${format(parseISO(t.date), "dd/MM/yyyy")}`}
                                size="small"
                                sx={{ fontSize: "0.6rem", mt: 0.5, bgcolor: "rgba(245,158,11,0.2)", color: "#f59e0b" }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#10b981", fontWeight: 600 }}>
                            {t.cash > 0 ? `₹${t.cash.toLocaleString("en-IN")}` : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#2563eb", fontWeight: 600 }}>
                            {t.bank > 0 ? `₹${t.bank.toLocaleString("en-IN")}` : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#f59e0b", fontWeight: 600 }}>
                            {t.fineReceived > 0 ? formatFine(t.fineReceived) : "-"}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                              <Tooltip title="View">
                                <IconButton size="small" onClick={() => handleViewTransaction(t)} sx={{ p: 0.5 }}>
                                  <VisibilityIcon fontSize="small" sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              {!t.isAutoGenerated && (
                                <>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleEditTransaction(t)} sx={{ p: 0.5 }}>
                                      <EditIcon fontSize="small" sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => handleDeleteTransaction(t)} sx={{ p: 0.5 }}>
                                      <DeleteIcon fontSize="small" sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    <TableRow sx={{ bgcolor: isDark ? "rgba(16,185,129,0.05)" : "rgba(16,185,129,0.03)" }}>
                      <TableCell colSpan={2}><Typography sx={{ fontWeight: 700 }}>Total</Typography></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#10b981" }}>₹{summary.cashIncome.toLocaleString("en-IN")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#2563eb" }}>₹{summary.bankIncome.toLocaleString("en-IN")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#f59e0b" }}>{formatFine(summary.fineReceived)}</TableCell>
                      <TableCell align="center">
                        <Chip label={`₹${summary.totalIncome.toLocaleString("en-IN")}`} size="small" sx={{ bgcolor: "rgba(16,185,129,0.2)", color: "#10b981", fontWeight: 700 }} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Expense Column */}
            <Box sx={{ minHeight: 400 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Party</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Cash</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Bank</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Fine</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", fontSize: "0.7rem" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allExpenseTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: isDark ? "#64748b" : "#94a3b8" }}>
                          No expense transactions
                        </TableCell>
                      </TableRow>
                    ) : (
                      allExpenseTransactions.map((t, idx) => (
                        <TableRow key={t.id || idx} hover>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: "0.6rem", bgcolor: "rgba(239,68,68,0.2)", color: "#ef4444" }}>
                                {t.partyName?.charAt(0) || "?"}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>{t.partyName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                              {t.description?.slice(0, 30) || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#ef4444", fontWeight: 600 }}>
                            {t.cash > 0 ? `₹${t.cash.toLocaleString("en-IN")}` : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#2563eb", fontWeight: 600 }}>
                            {t.bank > 0 ? `₹${t.bank.toLocaleString("en-IN")}` : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#f59e0b", fontWeight: 600 }}>
                            {t.finePaid > 0 ? formatFine(t.finePaid) : "-"}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                              <Tooltip title="View">
                                <IconButton size="small" onClick={() => handleViewTransaction(t)} sx={{ p: 0.5 }}>
                                  <VisibilityIcon fontSize="small" sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              {!t.isAutoGenerated && !isReadOnly && (
                                <>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleEditTransaction(t)} sx={{ p: 0.5 }}>
                                      <EditIcon fontSize="small" sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => handleDeleteTransaction(t)} sx={{ p: 0.5 }}>
                                      <DeleteIcon fontSize="small" sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    <TableRow sx={{ bgcolor: isDark ? "rgba(239,68,68,0.05)" : "rgba(239,68,68,0.03)" }}>
                      <TableCell colSpan={2}><Typography sx={{ fontWeight: 700 }}>Total</Typography></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#ef4444" }}>₹{summary.cashExpense.toLocaleString("en-IN")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#2563eb" }}>₹{summary.bankExpense.toLocaleString("en-IN")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "#f59e0b" }}>{formatFine(summary.finePaid)}</TableCell>
                      <TableCell align="center">
                        <Chip label={`₹${summary.totalExpense.toLocaleString("en-IN")}`} size="small" sx={{ bgcolor: "rgba(239,68,68,0.2)", color: "#ef4444", fontWeight: 700 }} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* ============ BOTTOM SUMMARY - Added Fine Closing Balance ============ */}
          <Box sx={{ p: 3, borderTop: `2px solid ${isDark ? "#334155" : "#e2e8f0"}`, bgcolor: isDark ? "#0f172a" : "#f8fafc" }}>
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? "#1e293b" : "#ffffff", textAlign: "center" }}>
                  <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>Net Profit/Loss</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: summary.netProfit >= 0 ? "#10b981" : "#ef4444" }}>
                    {formatCurrency(summary.netProfit)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? "#1e293b" : "#ffffff", textAlign: "center" }}>
                  <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>Closing Cash</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#10b981" }}>
                    {formatCurrency(summary.closingBalanceCash)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? "#1e293b" : "#ffffff", textAlign: "center" }}>
                  <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>Closing Bank</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#2563eb" }}>
                    {formatCurrency(summary.closingBalanceBank)}
                  </Typography>
                </Paper>
              </Grid>
              {/* In bottom summary section, add fine equivalent value */}
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? "#1e293b" : "#ffffff", textAlign: "center", border: `2px solid ${isDark ? "#475569" : "#cbd5e1"}` }}>
                  <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>Closing Fine</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#f59e0b" }}>
                    {formatFine(summary.closingBalanceFine)}
                  </Typography>
                  {/* 🔥 Show equivalent cash value */}
                  <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: 'block', mt: 0.5 }}>
                    @ ₹{metalRate}/gm = {formatCurrency(summary.closingBalanceFine * metalRate)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* ============ ADD/EDIT TRANSACTION DIALOG ============ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: isDark ? "#f1f5f9" : "#1e293b", borderBottom: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
          {editingTransaction
            ? "Edit Transaction"
            : newTransaction.type === "income"
              ? "Add Income (Customer)"
              : "Add Expense (Supplier)"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block", mb: 1 }}>Party</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  size="small"
                  variant={newTransaction.partyType === "manual" ? "contained" : "outlined"}
                  onClick={() => {
                    setNewTransaction(prev => ({ ...prev, partyType: "manual", partyName: "Manual Entry", customerId: null, supplierId: null }));
                    clearSelection();
                  }}
                >
                  Manual
                </Button>

                {/* Only show Customer button for Income transactions */}
                {newTransaction.type === "income" && (
                  <Button
                    size="small"
                    variant={newTransaction.partyType === "customer" ? "contained" : "outlined"}
                    onClick={() => {
                      setNewTransaction(prev => ({ ...prev, partyType: "customer", supplierId: null }));
                      setSelectedSupplier(null);
                      setSupplierQuery("");
                    }}
                  >
                    Customer
                  </Button>
                )}

                {/* Only show Supplier button for Expense transactions */}
                {newTransaction.type === "expense" && (
                  <Button
                    size="small"
                    variant={newTransaction.partyType === "supplier" ? "contained" : "outlined"}
                    onClick={() => {
                      setNewTransaction(prev => ({ ...prev, partyType: "supplier", customerId: null }));
                      setSelectedCustomer(null);
                      setCustomerQuery("");
                    }}
                  >
                    Supplier
                  </Button>
                )}
              </Stack>

              {/* Only show Customer search for Income */}
              {newTransaction.type === "income" && newTransaction.partyType === "customer" && (
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Customer"
                    value={customerQuery}
                    onChange={(e) => handleCustomerSearch(e.target.value)}
                    onFocus={(e) => {
                      customerAnchorRef.current = e.currentTarget;
                      if (customerQuery.trim()) {
                        handleCustomerSearch(customerQuery);
                      }
                    }}
                    placeholder="Type name, contact or shortcut..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: selectedCustomer && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={clearSelection}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {renderCustomerPopper()}
                  {selectedCustomer && renderSelectedPartyBalance()}
                </Box>
              )}

              {/* Only show Supplier search for Expense */}
              {newTransaction.type === "expense" && newTransaction.partyType === "supplier" && (
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Supplier"
                    value={supplierQuery}
                    onChange={(e) => handleSupplierSearch(e.target.value)}
                    onFocus={(e) => {
                      supplierAnchorRef.current = e.currentTarget;
                      if (supplierQuery.trim()) {
                        handleSupplierSearch(supplierQuery);
                      }
                    }}
                    placeholder="Type name, contact or shortcut..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: selectedSupplier && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={clearSelection}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {renderSupplierPopper()}
                  {selectedSupplier && renderSelectedPartyBalance()}
                </Box>
              )}
            </Box>

            <TextField fullWidth size="small" label="Bill Number" value={newTransaction.billNumber} disabled />
            <TextField
              fullWidth
              size="small"
              label="Transaction Date"
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={
                transactionDate !== format(new Date(), "yyyy-MM-dd")
                  ? `⚠️ Backdated transaction: ${format(parseISO(transactionDate), "dd/MM/yyyy")}`
                  : "Select the date when this transaction actually occurred"
              }
              FormHelperTextProps={{
                sx: {
                  color: transactionDate !== format(new Date(), "yyyy-MM-dd") ? '#f59e0b' : undefined,
                  fontWeight: transactionDate !== format(new Date(), "yyyy-MM-dd") ? 600 : undefined
                }
              }}
            />
            <TextField fullWidth size="small" label="Description" value={newTransaction.description} onChange={(e) => setNewTransaction(prev => ({ ...prev, description: e.target.value }))} multiline rows={2} />

            <Box>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block", mb: 1 }}>Payment Split (Cash + Bank = Total)</Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  size="small"
                  label="Cash"
                  type="number"
                  sx={{ flex: 1 }}
                  value={newTransaction.cashPortion}
                  onChange={handleCashPortionChange}
                />
                <TextField
                  size="small"
                  label="Bank"
                  type="number"
                  sx={{ flex: 1 }}
                  value={newTransaction.bankPortion}
                  onChange={handleBankPortionChange}
                />
              </Stack>
              <Box sx={{ mt: 1, textAlign: "right" }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? "#60a5fa" : "#2563eb" }}>
                  Total Amount: {formatCurrency(parseFloat(newTransaction.amount) || 0)}
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                label={newTransaction.type === "income" ? "Fine Received (gm)" : "Fine Paid (gm)"}
                type="number"
                sx={{ flex: 1 }}
                value={newTransaction.type === "income" ? newTransaction.fineReceived : newTransaction.finePaid || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (newTransaction.type === "income") {
                    setNewTransaction(prev => ({ ...prev, fineReceived: val }));
                  } else {
                    setNewTransaction(prev => ({ ...prev, finePaid: val }));
                  }
                }}
                helperText="Enter fine amount in grams (can be standalone)"
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Mode</InputLabel>
                <Select
                  value={newTransaction.paymentMode}
                  onChange={(e) => setNewTransaction(prev => ({ ...prev, paymentMode: e.target.value }))}
                  label="Mode"
                >
                  <MenuItem value="cash">Cash Only</MenuItem>
                  <MenuItem value="bank">Bank Only</MenuItem>
                  <MenuItem value="split">Split</MenuItem>
                  <MenuItem value="fine">Fine Only</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            {/* 🔥 NEW: Conversion Section */}
            <Box sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: isDark ? "rgba(245,158,11,0.05)" : "rgba(245,158,11,0.05)",
              border: `1px solid ${isDark ? "#475569" : "#cbd5e1"}`
            }}>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block", mb: 1, fontWeight: 600 }}>
                🔄 Cash ↔ Fine Conversion (Optional)
              </Typography>
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Conversion Type</InputLabel>
                  <Select
                    value={newTransaction.conversionType || "none"}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewTransaction(prev => ({
                        ...prev,
                        conversionType: val,
                        conversionAmount: "",
                        conversionNote: ""
                      }));
                    }}
                    label="Conversion Type"
                  >
                    <MenuItem value="none">No Conversion</MenuItem>
                    <MenuItem value="cashForFine">Cash → Fine (Customer pays cash)</MenuItem>
                    <MenuItem value="fineForCash">Fine → Cash (Customer pays fine)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label={newTransaction.conversionType === "cashForFine" ? "Cash Amount (₹)" : "Fine Amount (gm)"}
                  type="number"
                  sx={{ flex: 1 }}
                  value={newTransaction.conversionAmount || ""}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    const rate = metalRate || 15000;
                    let note = "";

                    if (newTransaction.conversionType === "cashForFine") {
                      const fine = val / rate;
                      note = `💰 ₹${val.toLocaleString()} → 💎 ${fine.toFixed(3)}gm fine (will be deducted from balance)`;
                    } else if (newTransaction.conversionType === "fineForCash") {
                      const cash = val * rate;
                      note = `💎 ${val}gm → 💰 ₹${cash.toLocaleString()} (will be added to balance)`;
                    }

                    setNewTransaction(prev => ({
                      ...prev,
                      conversionAmount: val,
                      conversionNote: note
                    }));
                  }}
                  helperText={
                    newTransaction.conversionType === "cashForFine"
                      ? `At ₹${metalRate}/gm: ${formatFine((parseFloat(newTransaction.conversionAmount) || 0) / metalRate)} fine will be deducted`
                      : newTransaction.conversionType === "fineForCash"
                        ? `At ₹${metalRate}/gm: ₹${((parseFloat(newTransaction.conversionAmount) || 0) * metalRate).toLocaleString()} cash will be added`
                        : "Select conversion type first"
                  }
                />
              </Stack>
              {newTransaction.conversionNote && (
                <Alert severity="info" sx={{ mt: 1, fontSize: '0.8rem' }}>
                  {newTransaction.conversionNote}
                </Alert>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitTransaction}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ============ VIEW TRANSACTION DIALOG ============ */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: `1px solid ${isDark ? "#334155" : "#e2e8f0"}` }}>
          Transaction Details
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* In view dialog, add conversion info */}
          {viewingTransaction && (
            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2"><strong>Bill No:</strong> {viewingTransaction.billNumber}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {viewingTransaction.type}</Typography>
              </Box>
              <Box>
                <Typography variant="body2"><strong>Party:</strong> {viewingTransaction.partyName}</Typography>
                <Typography variant="body2"><strong>Description:</strong> {viewingTransaction.description}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", p: 2, bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <strong>Cash:</strong> {formatCurrency(viewingTransaction.cash)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <strong>Bank:</strong> {formatCurrency(viewingTransaction.bank)}
                </Typography>
                {viewingTransaction.fineReceived > 0 && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#f59e0b" }}>
                    <strong>Fine Received:</strong> {formatFine(viewingTransaction.fineReceived)}
                  </Typography>
                )}
                {viewingTransaction.finePaid > 0 && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#f59e0b" }}>
                    <strong>Fine Paid:</strong> {formatFine(viewingTransaction.finePaid)}
                  </Typography>
                )}
              </Box>
              {/* 🔥 Show conversion details */}
              {viewingTransaction.conversionType && viewingTransaction.conversionAmount && viewingTransaction.metalRate && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Conversion Details:</strong>
                    <br />
                    {viewingTransaction.conversionType === "cashForFine" ? (
                      <>
                        💰 Cash Paid: ₹{viewingTransaction.conversionAmount.toLocaleString()}
                        <br />
                        💎 Fine Deducted: {formatFine(viewingTransaction.conversionAmount / viewingTransaction.metalRate)}
                        <br />
                        📊 Rate: ₹{viewingTransaction.metalRate}/gm
                        <br />
                        <em>Customer paid cash in lieu of fine</em>
                      </>
                    ) : (
                      <>
                        💎 Fine Paid: {formatFine(viewingTransaction.conversionAmount)}
                        <br />
                        💰 Cash Added: {formatCurrency(viewingTransaction.conversionAmount * viewingTransaction.metalRate)}
                        <br />
                        📊 Rate: ₹{viewingTransaction.metalRate}/gm
                        <br />
                        <em>Customer paid fine in lieu of cash</em>
                      </>
                    )}
                  </Typography>
                </Alert>
              )}
              {viewingTransaction.remark && <Typography variant="body2"><strong>Remark:</strong> {viewingTransaction.remark}</Typography>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ============ CLOSE DAY DIALOG ============ */}
      <Dialog open={closeDayDialogOpen} onClose={() => setCloseDayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Close Day - Verify Actual Balances</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Paper sx={{ p: 2, bgcolor: isDark ? "rgba(16,185,129,0.05)" : "rgba(16,185,129,0.03)", borderRadius: 2 }}>
              <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>Calculated Balances</Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Typography>Cash: {formatCurrency(summary.closingBalanceCash)}</Typography>
                <Typography>Bank: {formatCurrency(summary.closingBalanceBank)}</Typography>
                <Typography>Fine: {formatFine(summary.closingBalanceFine)}</Typography>
              </Box>
            </Paper>
            <TextField size="small" label="Actual Cash" type="number" value={actualBalances.cash} onChange={(e) => handleActualBalanceChange("cash", e.target.value)} />
            <TextField size="small" label="Actual Bank" type="number" value={actualBalances.bank} onChange={(e) => handleActualBalanceChange("bank", e.target.value)} />
            <TextField size="small" label="Actual Fine (gm)" type="number" value={actualBalances.fine} onChange={(e) => handleActualBalanceChange("fine", e.target.value)} />
            {(differences.cash !== 0 || differences.bank !== 0 || differences.fine !== 0) && (
              <Alert severity="warning">
                Difference: Cash {differences.cash > 0 ? "+" : ""}{formatCurrency(differences.cash)},
                Bank {differences.bank > 0 ? "+" : ""}{formatCurrency(differences.bank)},
                Fine {differences.fine > 0 ? "+" : ""}{formatFine(differences.fine)}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDayDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleConfirmCloseDay} disabled={isClosingDay}>
            {isClosingDay ? <CircularProgress size={20} /> : "Confirm Close"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isClosingDay && (
        <Box sx={{ position: "fixed", inset: 0, bgcolor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Closing day...</Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default JewelleryDaybook;